import { defineStore } from 'pinia'
import { ref, computed, nextTick } from 'vue'
import { useSupabaseClientStore } from './supaBaseClientStore'
import { useAuthStore } from './authStore'
import { useHeartbeatStore } from './heartbeatStore'
import { useLogger } from '../composables/useLogger'
import { usePostHog, USAGE_EVENTS } from '~/composables/usePostHog'
import { useFirebaseAnalytics } from '~/composables/useFirebaseAnalytics'
import { LOAD_CALENDAR_EXAMPLE } from '~/src/constants'
export const useCalendarStore = defineStore('calendar', () => {
  const logger = useLogger()
  const supabaseClientStore = useSupabaseClientStore()
  const authStore = useAuthStore()
  const heartbeatStore = useHeartbeatStore()
  const posthog = usePostHog()
  const analytics = useFirebaseAnalytics()

  const isContentLoaded = ref(false)
  const error = ref(null)

  const calendars = ref([])
  const calendarItems = ref([])
  
  const supabase = computed(() => supabaseClientStore.supabase)

  const userId = computed(() => authStore.userId)

  let realtimeSubscription = null
  let loadingPromise = null


  watch(userId, () => {
    logger.debug('useCalendarStore: userId changed, resetting store', { userId: userId.value })
    $reset()
    loadCalendarContent()
  })

  // Recovery function
  async function handleConnectivityRestored() {
    logger.info('Calendar: Connectivity restored - refreshing calendar data')
    try {
      await cleanupRealtimeSubscription()
      isContentLoaded.value = false
      await loadCalendarContent()
      setupRealtimeSubscription()
      logger.info('Calendar: Data refresh complete')
    } catch (e) {
      logger.logErrorCaught('Calendar: Failed to refresh data', 'CalendarStore.js', e)
      error.value = `Failed to refresh calendar data: ${e.message}`
    }
  }

  async function handleConnectivityLost() {
    logger.info('Calendar: Connectivity lost - removing realtime subscription')
    await cleanupRealtimeSubscription()
  }

  async function initializeStore() {
    logger.debug('Calendar: initializeStore', { 
      userId: userId.value,
      isContentLoaded: isContentLoaded.value,
      itemCount: calendarItems.value.length
    });
    
    if (!userId.value) {
      logger.debug('Calendar: No userId, skipping initializeStore');
      return;
    }
    
    try {
      // Even if we have content loaded, we need to ensure subscriptions are setup
      
      logger.debug('Calendar: Initializing store from scratch');
      await loadCalendarContent();
      setupRealtimeSubscription();
      if (process.client) {
        heartbeatStore.registerCallbackConnectivityRestored(handleConnectivityRestored);
        heartbeatStore.registerCallbackConnectivityLost(handleConnectivityLost);
      }
      logger.debug('Calendar: Store initialized with', calendarItems.value.length, 'items');
    } catch (e) {
      logger.logErrorCaught('Calendar: Failed to initialize store', 'CalendarStore.js', e);
      error.value = `Failed to initialize calendar store: ${e.message}`;
    }
  }

  async function loadCalendarContent() {
    logger.debug('Calendar: loadCalendarContent', { userId: authStore.userId })
    if (isContentLoaded.value) return
    if (loadingPromise) return loadingPromise
    
    loadingPromise = (async () => {
      if (!authStore.userLoggedIn) {
        calendarItems.value = []
        isContentLoaded.value = true
        return
      }
      try {
        logger.debug('Calendar: loadCalendarContent: ensureDefaultCalendar')
        await ensureDefaultCalendar()
        logger.debug('Calendar: loadCalendarContent: loadCalendars')
        await loadCalendars()
        logger.debug('Calendar: loadCalendarContent: loadCalendarItems')
        await loadCalendarItems()
        isContentLoaded.value = true
      } catch (e) {
        console.error('Error loading calendar content:', e)
        error.value = `Failed to load calendar content: ${e.message}`
        isContentLoaded.value = false
        throw e
      } finally {
        loadingPromise = null
      }
    })()
    
    return loadingPromise
  }

  async function ensureDefaultCalendar() {
    logger.debug('Calendar: ensureDefaultCalendar', { userId: authStore.userId })
    const { data, error } = await supabase.value
      .from('calendars')
      .select('id')
      .eq('user_id', authStore.userId)
      .eq('name', 'default')
      .single()

    if (error || !data) {
      // No default calendar exists, create one
      const { data: newCalendar, error: insertError } = await supabase.value
        .from('calendars')
        .insert({ name: 'default', user_id: authStore.userId })
        .select()
        .single()

      if (insertError) {
        logger.throwSupabaseError(insertError, 'Failed to create default calendar')
      }
      
      calendars.value = [newCalendar]
      
      // Create a welcome appointment in the newly created calendar
      if (LOAD_CALENDAR_EXAMPLE) {
        await createWelcomeAppointmentForCalendar(newCalendar.id)
      }
    } else {
      calendars.value = [data]
    }
  }

  async function loadCalendars() {
    if (!authStore.userId) {
      throw new Error('User ID not available')
    }
    const { data, error } = await supabase.value
      .from('calendars')
      .select('*')
      .eq('user_id', authStore.userId)

    if (error) logger.throwSupabaseError(error, 'Failed to load calendars')
    calendars.value = data
  }

  async function loadCalendarItems() {
    if (calendars.value.length === 0) {
      return
    }
    const { data, error } = await supabase.value
      .from('calendar_items')
      .select('*')
      .eq('calendar_id', calendars.value[0].id)

    if (error) logger.throwSupabaseError(error, 'Failed to load calendar items')
    calendarItems.value = data
  }

  async function addCalendarItem(item) {
    if (calendars.value.length === 0) {
      error.value = 'No calendar available. Please try reloading the page.'
      return
    }
    try {
      const { data, error: insertError } = await supabase.value
        .from('calendar_items')
        .insert({
          ...item,
          calendar_id: calendars.value[0].id
        })
        .select()

      if (insertError) logger.throwSupabaseError(insertError, 'Failed to add calendar item')

      if (!data || data.length === 0) {
        logger.throwSupabaseError(new Error('No data returned after insert'), 'Failed to add calendar item')
      }

      await posthog.capture(USAGE_EVENTS.CALENDAR_ITEM_ADDED, {
        calendar_item: data[0]
      })
      await analytics.logEvent(USAGE_EVENTS.CALENDAR_ITEM_ADDED, {
        calendar_item: data[0]
      })
    
      return data[0]
    } catch (e) {
      logger.logErrorCaught('Failed to add calendar item', 'CalendarStore.js', e)
      error.value = `Failed to add calendar item: ${e.message}`
      throw e
    }
  }

  async function updateCalendarItem(item) {
    const { data, error: updateError } = await supabase.value
      .from('calendar_items')
      .update(item)
      .eq('id', item.id)
      .select()

    if (updateError) {
      logger.error('Error updating calendar item:', updateError)
      error.value = 'Failed to update calendar item'
      logger.throwSupabaseError(updateError, 'Failed to update calendar item')
    }
    const index = calendarItems.value.findIndex(i => i.id === item.id)
    if (index !== -1) {
      calendarItems.value[index] = data[0]
    }
  }

  async function deleteCalendarItem(id) {
    const { error: deleteError } = await supabase.value
      .from('calendar_items')
      .delete()
      .eq('id', id)

    if (deleteError) {
      logger.error('Error deleting calendar item:', deleteError)
      error.value = 'Failed to delete calendar item'
      logger.throwSupabaseError(deleteError, 'Failed to delete calendar item')
    }
    calendarItems.value = calendarItems.value.filter(item => item.id !== id)
  }

  function setupRealtimeSubscription() {
    if (!calendars.value.length) {
      logger.error('No calendars available for real-time subscription')
      return
    }

    const calendarId = calendars.value[0].id

    try {
      realtimeSubscription = supabase.value
        .channel('calendar_items_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'calendar_items',
            filter: `calendar_id=eq.${calendarId}`
          },
          (payload) => {
            handleRealtimeUpdate(payload)
          }
        )
        .subscribe()
    } catch (error) {
      logger.logErrorCaught('Calendar: Failed to setup realtime subscription', 'CalendarStore.js', error)
    }
  }

  function handleRealtimeUpdate(payload) {
    const { eventType, new: newItem, old: oldItem } = payload

    switch (eventType) {
      case 'INSERT':
        if (!calendarItems.value.some(item => item.id === newItem.id)) {
          calendarItems.value.push(newItem)
        }
        break
      case 'UPDATE':
        const updateIndex = calendarItems.value.findIndex(item => item.id === newItem.id)
        if (updateIndex !== -1) {
          calendarItems.value[updateIndex] = newItem
        }
        break
      case 'DELETE':
        calendarItems.value = calendarItems.value.filter(item => item.id !== oldItem.id)
        break
    }
  }

  async function cleanupRealtimeSubscription() {
    if (realtimeSubscription) {
      try {
        // First unsubscribe from the channel
        await realtimeSubscription.unsubscribe()
        // Then remove the channel from Supabase client
        await supabase.value.removeChannel(realtimeSubscription)
      } catch (error) {
        logger.logErrorCaught('Calendar: Error cleaning up realtime subscription', 'CalendarStore.js', error)
      } finally {
        realtimeSubscription = null
      }
    }
  }

  function $reset() {
    // We no longer clean up subscriptions during normal navigation
    // This ensures real-time updates continue to flow without interruption
    // Only the heartbeat mechanism will handle subscription management based on connectivity

    // Log that we're preserving both data and subscriptions
    logger.debug('Calendar: Cleanup called - data and subscriptions preserved');
  }

  // Create a welcome appointment for a newly created calendar
  async function createWelcomeAppointmentForCalendar(calendarId) {
    try {
      // Set the appointment to start now
      const now = new Date()
      
      // Calculate end time (2 hours from now)
      const endTime = new Date(now)
      endTime.setHours(endTime.getHours() + 2)
      
      // Check if end time crosses midnight and adjust if needed
      const midnight = new Date(now)
      midnight.setHours(23, 59, 59, 999)
      
      // Use the earlier of the two times (2 hours from now or midnight)
      const finalEndTime = endTime > midnight ? midnight : endTime
      
      const welcomeAppointment = {
        description: 'Example appointment.',
        starts_at: now.toISOString(),
        ends_at: finalEndTime.toISOString(),
        is_all_day: false,
        calendar_id: calendarId
      }
      
      // Insert directly to database rather than using addCalendarItem
      // to avoid any circular dependencies (since addCalendarItem checks for calendars)
      const { error: insertError } = await supabase.value
        .from('calendar_items')
        .insert(welcomeAppointment)
      
      if (insertError) {
        logger.logErrorCaught('Calendar: Failed to create welcome appointment', 'CalendarStore.js', insertError)
      } else {
        logger.debug('Calendar: Created welcome appointment for new calendar')
      }
    } catch (e) {
      logger.logErrorCaught('Calendar: Failed to create welcome appointment', 'CalendarStore.js', e)
    }
  }

  return {
    calendarItems,
    isContentLoaded,
    error,
    addCalendarItem,
    updateCalendarItem,
    deleteCalendarItem,
    initializeStore,
    cleanup: $reset
  }
})
